import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { RecentEntityType, RecentsConstants } from 'src/services/recents/RecentsConstants';
import { hybridScore, onRecentsChange, RecentEntry, RecentsService } from 'src/services/recents/RecentsService';
import type { RecentsHydrator } from 'src/services/recents/hydrators/types';

/** A hydrated recent entry — the raw DDB row resolved against a catalog. */
export interface HydratedRecent<T> {
  /** The resolved entity from the hydrator. */
  data: T;
  /** Hybrid recency+frequency score used for ranking. */
  score: number;
  /** ISO 8601 timestamp of last visit — for chronological sorting across types. */
  visitedAt: string;
}

export interface UseRecentsResult<T> {
  /** Hydrated, ranked, capped recents. Empty array while loading or on error. */
  recents: T[];
  /** Same entries with scores attached, for cross-type merging. */
  scoredRecents: HydratedRecent<T>[];
  /** `true` while the initial DDB read + hydration is in flight. */
  isLoading: boolean;
  /** Force a re-fetch (e.g. after a write). */
  refresh: () => void;
}

/**
 * Generic hook that fetches raw recents for `entityType`, hydrates each entry
 * via `hydrator.resolve()`, ranks by hybrid score, and caps at the per-type limit.
 *
 * Entries that fail hydration (return `null`) are silently dropped — this is
 * the mechanism that filters out deleted apps, removed profile links, etc.
 */
export function useRecents<T>(entityType: RecentEntityType, hydrator: RecentsHydrator<T>): UseRecentsResult<T> {
  const { CognitoSub } = useAuth();
  const [rawEntries, setRawEntries] = useState<RecentEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0);

  const refresh = useCallback(() => setFetchKey((k) => k + 1), []);

  // Fetch raw entries from DDB.
  useEffect(() => {
    if (!CognitoSub) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);

    RecentsService.getRecentsOfType(CognitoSub, entityType).then((entries) => {
      if (!cancelled) {
        setRawEntries(entries);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [CognitoSub, entityType, fetchKey]);

  // Auto-refresh when a write lands for this entity type.
  useEffect(
    () =>
      onRecentsChange((type) => {
        if (type === entityType) refresh();
      }),
    [entityType, refresh]
  );

  // Hydrate, rank, cap.
  const scoredRecents = useMemo<HydratedRecent<T>[]>(() => {
    if (rawEntries.length === 0) return [];

    const weights = RecentsConstants.WEIGHTS_BY_TYPE[entityType];
    const cap = RecentsConstants.CAP_BY_TYPE[entityType];
    const now = Date.now();

    const hydrated: HydratedRecent<T>[] = [];
    for (const entry of rawEntries) {
      const resolved = hydrator.resolve(entry.entityId);
      // Skip async hydrators in this phase — all Phase-1 hydrators are sync.
      if (resolved === null || resolved instanceof Promise) continue;
      hydrated.push({
        data: resolved,
        score: hybridScore(entry.visitedAt, entry.visitCount, weights, now),
        visitedAt: entry.visitedAt
      });
    }

    hydrated.sort((a, b) => b.score - a.score);
    const capped = hydrated.slice(0, cap);
    return capped;
  }, [rawEntries, hydrator, entityType]);

  const recents = useMemo(() => scoredRecents.map((h) => h.data), [scoredRecents]);

  return { recents, scoredRecents, isLoading, refresh };
}
